import React, { useEffect, useMemo, useState } from 'react';
import {
  DriveNode,
  FolderNode,
  sortNodes,
  toggleFolderIds,
  useDriveHierarchy,
  indexFolders,
  folderPathLabel,
  countSelectedLeaves,
} from './DocumentSidebar';

// Mirrors RDF-Agent /api/chat/refine and the refine modal in app.js — change both.

export interface RefineFolder {
  id: string;
  name: string | null;
  /** This folder's own match score (0–100). */
  score?: number;
}

export interface RefineScored {
  score: number | null;
  /** Best of selected / unselected folders; differs from score only when extra_folders is non-empty. */
  score_all?: number | null;
  /** "better": unselected folders match clearly better; "also": they are strong sources too. */
  extra_kind?: 'better' | 'also' | null;
  /** Unselected folders where this question matches clearly better. */
  extra_folders?: RefineFolder[];
}

export interface RefineSuggestion extends RefineScored {
  question: string;
  rationale: string;
}

export interface RefineUsage {
  llm?: string;
  total_tokens?: number;
  cost_gbp?: number;
}

export interface RefineData {
  original: RefineScored & { question: string };
  suggestions: RefineSuggestion[];
  refine_failed?: boolean;
  usage?: RefineUsage;
}

export interface RefineResult {
  data: RefineData | null;
  /** HTTP status when the request didn't succeed (e.g. 402, 429), 0 on network error/timeout. */
  status?: number;
}

export interface RefineChoice {
  question: string;
  /** Complete folder selection to search with. */
  folderIds: Set<string>;
  /** Labels of folders added compared with the chat's selection when the pop-up opened. */
  addedLabels: string[];
}

interface Props {
  data: RefineData;
  initialSelectedIds: Set<string>;
  /** Re-run refinement for an edited question and/or folder selection. */
  onRecheck: (question: string, folderIds: Set<string>) => Promise<RefineResult>;
  onSelect: (choice: RefineChoice | null) => void;
}

function scoreBand(score: number): string {
  if (score >= 70) return 'high';
  if (score >= 40) return 'mid';
  return 'low';
}

export function folderLabel(f: RefineFolder): string {
  return f.name || 'another folder';
}

function sameIds(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

const RefineOption: React.FC<{
  question: string;
  rationale?: string;
  scored: RefineScored;
  isOriginal: boolean;
  onPick: () => void;
  onEdit: () => void;
}> = ({ question, rationale, scored, isOriginal, onPick, onEdit }) => {
  const pct = Math.max(0, Math.min(100, Number(scored.score) || 0));
  const extras = scored.extra_folders || [];
  return (
    <div className={`ai-refine-option${isOriginal ? ' ai-refine-option-original' : ''}`}>
      <button type="button" className="ai-refine-option-main" onClick={onPick}>
        <span className="ai-refine-option-body">
          <span className="ai-refine-option-tag">{isOriginal ? 'Your question' : 'Suggested'}</span>
          <span className="ai-refine-option-question">{question}</span>
          {rationale && <span className="ai-refine-option-rationale">{rationale}</span>}
          {extras.length > 0 && (
            <span className="ai-refine-option-extra">
              {scored.extra_kind === 'better'
                ? <>Better match ({scored.score_all}%) if you also search: {extras.map(folderLabel).join(', ')}</>
                : <>Also relevant: {extras.map(f => f.score != null ? `${folderLabel(f)} (${f.score}%)` : folderLabel(f)).join(', ')}</>}
              <span className="ai-refine-option-extra-hint"> — picking this adds {extras.length > 1 ? 'them' : 'it'}</span>
            </span>
          )}
        </span>
        <span className={`ai-refine-score ai-refine-score-${scoreBand(pct)}`}>
          <span className="ai-refine-score-value">{scored.score == null ? '—' : `${pct}%`}</span>
          <span className="ai-refine-score-bar"><span style={{ width: `${pct}%` }} /></span>
          <span className="ai-refine-score-caption">match</span>
        </span>
      </button>
      <button type="button" className="ai-refine-edit" onClick={onEdit} title="Copy into the question box to edit">
        ✎ Edit
      </button>
    </div>
  );
};

export const RefineModal: React.FC<Props> = ({ data: initialData, initialSelectedIds, onRecheck, onSelect }) => {
  const { nodes, loading: treeLoading, error: treeError } = useDriveHierarchy();
  const [data, setData] = useState<RefineData>(initialData);
  const [question, setQuestion] = useState(initialData.original.question);
  const [workingIds, setWorkingIds] = useState<Set<string>>(() => new Set(initialSelectedIds));
  // What the current cards were scored against — drives the "check again" hint
  const [checkedQuestion, setCheckedQuestion] = useState(initialData.original.question);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set(initialSelectedIds));
  const [checking, setChecking] = useState(false);
  const [slow, setSlow] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const suggestedIds = useMemo(
    () => new Set([data.original, ...data.suggestions].flatMap(o => (o.extra_folders || []).map(f => f.id))),
    [data],
  );
  const [foldersOpen, setFoldersOpen] = useState(() =>
    suggestedIds.size > 0 && !(typeof window !== 'undefined' && window.innerWidth <= 600));

  const folderIndex = useMemo(() => indexFolders(nodes), [nodes]);
  // Expand the ancestors of suggested folders so the user can see them
  const openIds = useMemo(() => {
    const ids = new Set<string>();
    for (const id of suggestedIds) {
      let curr = folderIndex.get(id)?.parentId ?? null;
      while (curr) {
        ids.add(curr);
        curr = folderIndex.get(curr)?.parentId ?? null;
      }
    }
    return ids;
  }, [suggestedIds, folderIndex]);

  useEffect(() => {
    if (!checking) return;
    const t = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(t);
  }, [checking]);

  const dirty = question.trim() !== checkedQuestion.trim() || !sameIds(workingIds, checkedIds);

  const recheck = async () => {
    const q = question.trim();
    if (!q || checking) return;
    if (workingIds.size === 0) {
      setNotice('Select at least one folder to search.');
      return;
    }
    setChecking(true);
    setSlow(false);
    setNotice(null);
    const idsUsed = new Set(workingIds);
    const result = await onRecheck(q, idsUsed);
    setChecking(false);
    setSlow(false);
    if (result.data?.suggestions?.length) {
      setData(result.data);
      setCheckedQuestion(q);
      setCheckedIds(idsUsed);
    } else if (result.status === 429) {
      setNotice('Please wait a moment before checking again.');
    } else if (result.status === 402) {
      onSelect(null);  // spend cap reached — the chat shows the message
    } else {
      setNotice("Couldn't check that wording — the previous results are still shown.");
    }
  };

  const pick = (q: string, scored: RefineScored) => {
    const folderIds = new Set(workingIds);
    const backendLabels = new Map<string, string>();
    for (const f of scored.extra_folders || []) {
      folderIds.add(f.id);
      backendLabels.set(f.id, folderLabel(f));
    }
    // Label only the top-most added folders (ticking a parent adds its children too)
    const added = [...folderIds].filter(id => !initialSelectedIds.has(id));
    const addedSet = new Set(added);
    const topAdded = added.filter(id => {
      const parent = folderIndex.get(id)?.parentId;
      return !(parent && addedSet.has(parent));
    });
    const addedLabels = topAdded.map(id =>
      backendLabels.get(id) || folderPathLabel(id, folderIndex) || 'another folder');
    onSelect({ question: q, folderIds, addedLabels });
  };

  const handleToggle = (node: DriveNode, checked: boolean) =>
    setWorkingIds(prev => toggleFolderIds(prev, node, checked));

  return (
    <div className="ai-modal-overlay" onClick={() => onSelect(null)}>
      <div className="ai-llm-modal ai-refine-modal" onClick={e => e.stopPropagation()}>
        <div className="ai-llm-modal-header">
          <h3>Which question best matches what you mean?</h3>
          <p>Pick a question, or edit the wording and folders below and check again.</p>
        </div>

        <div className="ai-refine-ask">
          <textarea
            className="ai-refine-ask-input"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); recheck(); }
            }}
            rows={2}
            disabled={checking}
            aria-label="Question to check"
          />
          <button
            type="button"
            className="ai-refine-ask-btn"
            onClick={recheck}
            disabled={checking || !question.trim()}
          >
            {checking ? 'Checking…' : 'Check again'}
          </button>
        </div>
        {(checking || notice || dirty) && (
          <p className={`ai-refine-ask-status${notice ? ' is-notice' : ''}`} role="status">
            {checking
              ? (slow ? 'Still working… (the first request after a quiet spell can be slow)' : 'Checking how well each version matches…')
              : notice || 'Check again to see updated matches for your edits.'}
          </p>
        )}

        <div className={`ai-refine-options${checking ? ' is-busy' : ''}`}>
          <RefineOption
            question={data.original.question}
            scored={data.original}
            isOriginal
            onPick={() => pick(data.original.question, data.original)}
            onEdit={() => setQuestion(data.original.question)}
          />
          {data.suggestions.map(s => (
            <RefineOption
              key={s.question}
              question={s.question}
              rationale={s.rationale}
              scored={s}
              isOriginal={false}
              onPick={() => pick(s.question, s)}
              onEdit={() => setQuestion(s.question)}
            />
          ))}
        </div>

        <div className="ai-refine-folders">
          <button
            type="button"
            className="ai-refine-folders-toggle"
            onClick={() => setFoldersOpen(o => !o)}
            aria-expanded={foldersOpen}
          >
            <span style={{ transform: foldersOpen ? 'rotate(90deg)' : undefined }} className="ai-refine-folders-chevron">▶</span>
            Folders to search ({treeLoading ? workingIds.size : countSelectedLeaves(nodes, workingIds)} selected)
            {suggestedIds.size > 0 && <span className="ai-tree-suggested">{suggestedIds.size} suggested</span>}
          </button>
          {foldersOpen && (
            <div className="ai-refine-folders-body">
              {treeLoading && <p className="ai-sidebar-hint"><i>Loading library…</i></p>}
              {treeError && <p className="ai-sidebar-hint">Couldn't load the library.</p>}
              {!treeLoading && !treeError && (
                <ul className="ai-tree">
                  {sortNodes(nodes.filter(n => n.type === 'folder')).map(n => (
                    <FolderNode
                      key={n.id}
                      node={n}
                      level={0}
                      selectedIds={workingIds}
                      onToggle={handleToggle}
                      highlightIds={suggestedIds}
                      openIds={openIds}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <button className="ai-llm-cancel" onClick={() => onSelect(null)}>Cancel</button>
      </div>
    </div>
  );
};
